/**
 * Admin page for configuring who can create quotes (REQ-02).
 * A single admin-editable threshold: the minimum role allowed to create a quote
 * and add quote line items. Surfaced here so it can be tested and tuned without
 * a code change.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress,
  MenuItem, Snackbar, TextField, Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { getQuoteCreatePolicy, updateQuoteCreatePolicy } from '../../../services/quoteService';
import { THRESHOLD_ROLE_OPTIONS } from '../../../constants/roles';

export const QuotePermissionsPage = () => {
  const [minRole, setMinRole] = useState<string>('technician');
  const [savedRole, setSavedRole] = useState<string>('technician');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const p = await getQuoteCreatePolicy();
      setMinRole(p.minRole);
      setSavedRole(p.minRole);
    } catch {
      setSnack({ open: true, message: 'Failed to load quote permissions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPolicy(); }, [fetchPolicy]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateQuoteCreatePolicy(minRole);
      setMinRole(updated.minRole);
      setSavedRole(updated.minRole);
      setSnack({ open: true, message: 'Quote permissions updated', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Failed to update quote permissions', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  const dirty = minRole !== savedRole;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Quote Permissions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 620 }}>
        Set the minimum role allowed to create quotes and add quote line items. Users below this
        level will not see the &ldquo;New Quote&rdquo; button. The internal/external routing and the
        approval workflow are unaffected.
      </Typography>

      <Card variant="outlined" sx={{ maxWidth: 420, borderRadius: 2 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            size="small"
            label="Minimum role to create quotes"
            value={minRole}
            onChange={(e) => setMinRole(e.target.value)}
          >
            {THRESHOLD_ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{ alignSelf: 'flex-end', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            Save
          </Button>
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
